import React from "react";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import "./swal-globals.css";

import boltIcon from "../../img/boltIcon.svg";
import warningIcon from "../../img/warningIcon.svg";

/**
 * MetricModal component
 * 
 * Wraps children with a click handler to pop up a SweetAlert modal.
 * Modifies the modal to use custom background, and specific styles for
 * confirm/cancel/question in line with the product spec.
 */
const MetricModal = ({
  title,
  question,
  yesText,
  noText,
  onSuccess,
  onCancel,
  onOpen,
  onClose,
  children,
  color,
}) => {
  const theme = useTheme();

  // styledQuestion ensures proper HTML <span> wrapping & styling even if
  // question prop is already HTML (as from MetricItem)
  const styledQuestion = question
    ? `<span class="custom-metric-modal-question">${question}</span>`
    : "";

  const showAlert = () => {
    onOpen?.();

    Swal.fire({
      title,
      // Use styled html for the question
      html: styledQuestion,
      ...(
        yesText === "Remove Metric"
          ? {
              imageUrl: warningIcon,
              imageWidth: 71,
              imageHeight: 71,
              imageAlt: "Warning Icon",
            }
          : {
              imageUrl: boltIcon,
              imageWidth: 71,
              imageHeight: 71,
              imageAlt: "Bolt Icon",
            }
      ),
      iconColor: theme?.palette[color ?? "secondary"].focus,
      confirmButtonColor: "#FFFFFF",
      cancelButtonColor: "rgba(15, 23, 42, 0.03)",
      ...(noText && { showCancelButton: true }),
      ...(yesText && { confirmButtonText: yesText }),
      ...(noText && { cancelButtonText: noText }),
      reverseButtons: true,
      customClass: {
        container: "swal-container",
        popup: "custom-metric-modal-bg",
        confirmButton: "custom-metric-modal-confirm",
        cancelButton: "custom-metric-modal-cancel",
        htmlContainer: "custom-metric-modal-question-wrap",
        actions: "custom-metric-modal-actions"
      },
    }).then((result) => {
      if (result.isConfirmed) {
        onSuccess?.();
      } else if (result.isDismissed) {
        onCancel?.();
      }
      onClose?.();
    });
  };

  const handleClick = (event) => {
    if (!event.target.disabled) {
      showAlert();
    }
    event.stopPropagation();
  };

  return (
    <div>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child, {
          onClick: handleClick,
        });
      })}
      {/* Custom style is provided globally below */}
      <style>
        {`
          .custom-metric-modal-bg.swal2-popup {
            border-radius: 24px !important;
            background: linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%) !important;
            box-shadow: 0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7) !important;
            padding: 1.75rem !important;
            width: 100% !important;
            max-width: 600px !important;
            backdrop-filter: blur(18px) saturate(160%) !important;
            border: 1px solid rgba(255,255,255,0.6) !important;
          }
          @media (max-width: 1280px) {
            .custom-metric-modal-bg.swal2-popup {
              max-width: 90% !important;
            }
          }
          /* MetricModal action button container and gap */
          .custom-metric-modal-actions.swal2-actions {
            display: flex !important;
            gap: 2rem !important;
            justify-content: center !important;
            align-items: center !important;
            width: 100% !important;
          }
          /* Save (confirm) button: white background, #344054 text */
          .custom-metric-modal-confirm.swal2-confirm {
            background: #fff !important;
            color: #344054 !important;
            border: 1px solid #E9ECEF !important;
            box-shadow: 0 0.685293px 1.37059px rgba(0,0,0,0.05) !important;
            font-weight: 500 !important;
            font-size: 14px !important;
            text-transform: none !important;
            border-radius: 4px !important;
            outline: none !important;
            padding: 5px 30px !important;
            transition: background 0.2s, color 0.2s;
            height: 35px !important;
          }
          .custom-metric-modal-confirm.swal2-confirm:focus {
            outline: 2px solid #344054 !important;
          }
          /* Cancel button matches ExploreCustomMetricsTypes 3106-3115 */
          .custom-metric-modal-cancel.swal2-cancel {
            background: rgba(15, 23, 42, 0.03) !important;
            color: #475467 !important;
            font-weight: 500 !important;
            font-size: 14px !important;
            border: 0.685293px solid rgba(15, 23, 42, 0.10) !important;
            box-shadow: 0px 0.685293px 1.37059px rgba(0, 0, 0, 0.05) !important;
            text-transform: none !important;
            border-radius: 3px !important;
            outline: none !important;
            padding: 5px 30px !important;
            transition: background 0.2s, color 0.2s;
            height: 35px !important;
          }
          .custom-metric-modal-cancel.swal2-cancel:focus  {
            outline: 2px solid #344054 !important;
          }
          .custom-metric-modal-cancel.swal2-cancel:hover {
            background: rgba(15, 23, 42, 0.06) !important;
            color: #475467 !important;
          }
          /* Style the custom icon's image if needed */
          .swal2-image {
            margin-bottom: 16px !important;
          }

          .custom-metric-modal-question,
          .custom-metric-modal-question-wrap {
            font-family: 'DM Sans', 'DM Sans Placeholder', Arial, Helvetica, sans-serif !important;
            font-style: normal !important;
            font-weight: 400 !important;
            font-size: 14px !important;
            line-height: 20px !important;
            color: #475467 !important;
          }
          /* htmlContainer class targets the sweetalert2 question text container */
          .custom-metric-modal-question-wrap {
            padding-top: 2px;
          }
        `}
      </style>
    </div>
  );
};

export default MetricModal;
